import React, { useState, useEffect, useMemo, useRef } from "react";
import { UseFormReturn } from "react-hook-form";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormService, FormField } from "@shared/services/FormServices";
import {
  useInviteUser,
  useRoleOptions,
  useUpdateUser,
  useDepartmentOptions,
} from "@features/onboarding/utility-setup/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { UserItem } from "@features/onboarding/utility-setup/types";

interface UserFormProps {
  onClose?: () => void;
  editingUser?: UserItem | null;
}

const UserForm: React.FC<UserFormProps> = ({ onClose, editingUser }) => {
  const [formLoading, setFormLoading] = useState(false);
  const [userDepartmentCodes, setUserDepartmentCodes] = useState<
    Record<number, string[]>
  >({});

  const utilityId = useRemoteUtilityId();
  const isFormPopulated = useRef(false);
  const formRef = useRef<UseFormReturn<any> | null>(null);

  const { data: departmentData, isLoading: isDeptLoading } =
    useDepartmentOptions();
  const isEditMode = !!editingUser;

  // Set initial department codes for edit mode
  useEffect(() => {
    if (
      isEditMode &&
      editingUser &&
      departmentData &&
      Object.keys(userDepartmentCodes).length === 0
    ) {
      const depts = editingUser.extraDataMap?.department;
      if (depts && Array.isArray(depts) && depts.length > 0) {
        const deptCodes = depts
          .map((deptName: string) => {
            const dept = departmentData.find((d: any) => d.name === deptName);
            return dept?.code;
          })
          .filter(Boolean);

        console.log(
          "Setting initial department codes for edit mode:",
          deptCodes,
        );
        setUserDepartmentCodes({ 0: deptCodes });
      }
    }
  }, [isEditMode, editingUser, departmentData]);

  // Fetch roles for ALL unique departments across all users
  const allDepartmentCodes = useMemo(() => {
    const allCodes = Object.values(userDepartmentCodes).flat();
    return [...new Set(allCodes)];
  }, [userDepartmentCodes]);

  const { data: roleData, isLoading: isRoleLoading } =
    useRoleOptions(allDepartmentCodes);

  const { mutate: inviteUser } = useInviteUser();
  const updateUserMutation = useUpdateUser();

  // Transform department data to options
  const departmentOptions = useMemo(() => {
    return (
      departmentData?.map((dept: any) => ({
        value: dept.name,
        label: dept.name,
        code: dept.code,
      })) ?? []
    );
  }, [departmentData]);

  // Get role options for a specific user index
  const getRoleOptionsForUser = (userIndex: number) => {
    const userDeptCodes = userDepartmentCodes[userIndex] || [];

    if (!roleData || userDeptCodes.length === 0) {
      return [];
    }
    const userRoles: Array<{ value: string; label: string }> = [];

    roleData.forEach((dept) => {
      const deptCode = dept.department || dept.code;

      if (userDeptCodes.includes(deptCode)) {
        dept.roles?.forEach((role: any) => {
          if (!role.isActive) return; // skip inactive roles

          if (!userRoles.some((r) => r.value === role.roleName)) {
            userRoles.push({
              value: role.roleName,
              label: role.roleName,
            });
          }
        });
      }
    });

    return userRoles;
  };

  const transformedInitialData = useMemo(() => {
    if (!editingUser) return { users: [] };

    const department = Array.isArray(editingUser.extraDataMap?.department)
      ? editingUser.extraDataMap.department
      : editingUser.extraDataMap?.department
        ? [editingUser.extraDataMap.department]
        : [];

    const role = Array.isArray(editingUser.roleCode)
      ? editingUser.roleCode
      : editingUser.roleCode
        ? [editingUser.roleCode]
        : [];

    return {
      users: [
        {
          fullName: editingUser.user?.name || "",
          email: editingUser.user?.email || "",
          department: department,
          role: role,
        },
      ],
    };
  }, [editingUser]);

  // Define form fields with function-based itemFields
  const formFields: FormField[] = [
    {
      name: "users",
      label: isEditMode ? "Edit User" : "Add Users",
      type: "form-array",
      required: true,
      fullWidth: true,
      minItems: 1,
      maxItems: undefined,
      addButtonText: "Add User",
      emptyMessage: "No users added yet. Add at least one user.",
      showAddButton: true,
      showRemoveButton: true,
      helperText: isEditMode
        ? "Update the user information below"
        : "Add one or more users to invite to the system",
      itemFields: (index: number) => {
        const roleOptions = getRoleOptionsForUser(index);
        const userDeptCodes = userDepartmentCodes[index] || [];

        const formValues = formRef.current?.getValues();
        const currentUser = formValues?.users?.[index];
        const selectedRoles = currentUser?.role || [];

        const isEmailOptional = selectedRoles.some((role: string) =>
          ["Field Force Technician", "Meter Reader"].includes(role),
        );

        return [
          {
            name: "department",
            label: "Department",
            type: "multi-select",
            required: true,
            placeholder: "Select departments",
            options: departmentOptions,
            searchable: true,
            clearable: true,
            closeOnSelect: false,
            maxDisplayItems: 2,
          },
          {
            name: "role",
            label: "Role",
            type: "multi-select",
            required: true,
            placeholder:
              userDeptCodes.length > 0
                ? "Select roles"
                : "Select departments first",
            options: roleOptions,
            searchable: true,
            clearable: true,
            closeOnSelect: false,
            maxDisplayItems: 2,
            disabled: userDeptCodes.length === 0 || isRoleLoading,
          },
          {
            name: "fullName",
            label: "Full Name",
            type: "text",
            required: true,
            placeholder: "Enter full name",
            maxLength: 100,
          },
          {
            name: "email",
            label: "Email Address",
            type: "email",
            required: !isEmailOptional,
            placeholder: "user@example.com",
            disabled: isEditMode && index === 0,
          },
        ];
      },
      className: "[&_.grid]:!grid-cols-2", // Force exactly 2 columns to put department/role on line 1, name/email on line 2
      classes: {
        container: "w-full",
      },
    },
  ];

  const form: UseFormReturn<any> = FormService.createForm({
    fields: formFields,
    defaultValues: isEditMode
      ? transformedInitialData
      : {
          users: [
            {
              fullName: "",
              email: "",
              department: [],
              role: [],
            },
          ],
        },
    mode: "onSubmit",
    layout: "vertical",
  });

  const watchedUsers = form.watch("users");
  formRef.current = form;

  // Watch for department changes
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name && name.includes(".department")) {
        const users = value.users || [];
        const match = name.match(/users\.(\d+)\.department/);
        const userIndex = match ? parseInt(match[1], 10) : 0;
        const currentUser = users[userIndex];

        if (
          currentUser?.department &&
          Array.isArray(currentUser.department) &&
          currentUser.department.length > 0
        ) {
          const deptCodes = currentUser.department
            .map((deptName: string) => {
              const dept = departmentData?.find(
                (d: any) => d.name === deptName,
              );
              return dept?.code;
            })
            .filter(Boolean);

          const currentCodes = userDepartmentCodes[userIndex] || [];
          const codesChanged =
            deptCodes.length !== currentCodes.length ||
            !deptCodes.every((code: string) => currentCodes.includes(code));

          if (codesChanged) {
            setUserDepartmentCodes((prev) => ({
              ...prev,
              [userIndex]: deptCodes,
            }));

            form.setValue(`users.${userIndex}.role`, []);
          }
        } else {
          setUserDepartmentCodes((prev) => {
            const updated = { ...prev };
            delete updated[userIndex];
            return updated;
          });

          form.setValue(`users.${userIndex}.role`, []);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [form, departmentData, userDepartmentCodes]);

  // Populate form for edit mode
  useEffect(() => {
    if (
      isEditMode &&
      transformedInitialData &&
      editingUser &&
      roleData &&
      !isFormPopulated.current
    ) {
      setTimeout(() => {
        form.setValue("users", transformedInitialData.users);
        form.trigger();
        isFormPopulated.current = true;
      }, 100);
    }
  }, [isEditMode, transformedInitialData, editingUser, roleData]);

  // Reset populated flag
  useEffect(() => {
    if (editingUser) {
      isFormPopulated.current = false;
    }

    return () => {
      isFormPopulated.current = false;
    };
  }, [editingUser]);

  const transformToApiPayload = (formData: any) => {
    if (!formData.users || formData.users.length === 0) {
      return [];
    }

    return formData.users.map((user: any) => ({
      department: Array.isArray(user.department)
        ? user.department
        : [user.department],
      gender: "m",
      role_code: Array.isArray(user.role) ? user.role : [user.role],
      utility: utilityId,
      user: {
        email: user.email,
        name: user.fullName,
        tnc_accepted: true,
      },
    }));
  };

  const handleSubmit = async (data: any) => {
    setFormLoading(true);

    try {
      const payload = transformToApiPayload(data);

      if (isEditMode && editingUser) {
        const editPayload = {
          department: payload[0].department,
          gender: payload[0].gender,
          role_code: payload[0].role_code,
          user: {
            email: payload[0].user.email,
            name: payload[0].user.name,
          },
          profile_id: editingUser?.id,
          active: true,
          is_setup_completed: false,
        };

        updateUserMutation.mutate(
          {
            id: editingUser.user.id,
            payload: editPayload,
          },
          {
            onSuccess: () => {
              form.reset();
              isFormPopulated.current = false;
              setUserDepartmentCodes({});
              onClose?.();
            },
            onError: (error) => {
              console.error("Error updating user:", error);
            },
            onSettled: () => {
              setFormLoading(false);
            },
          },
        );
      } else {
        inviteUser(payload, {
          onSuccess: () => {
            form.reset();
            setUserDepartmentCodes({});
            onClose?.();
          },
          onError: (error) => {
            console.error("Error inviting users:", error);
          },
          onSettled: () => {
            setFormLoading(false);
          },
        });
      }
    } catch (error) {
      console.error("Error in form submission:", error);
      setFormLoading(false);
    }
  };

  const handleCancel = () => {
    form.reset();
    setUserDepartmentCodes({});
    isFormPopulated.current = false;
    onClose?.();
  };

  const renderCustomFooter = () => (
    <div className="flex flex-col sm:flex-row gap-2 justify-end pt-6 border-t border-gray-200">
      <button
        type="button"
        onClick={handleCancel}
        disabled={formLoading}
        className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
      >
        Cancel
      </button>
      <button
        type="submit"
        disabled={formLoading || isDeptLoading}
        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
      >
        {formLoading && (
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
        )}
        {formLoading
          ? isEditMode
            ? "Updating..."
            : "Sending..."
          : isEditMode
            ? "Update User"
            : "Send Invite"}
      </button>
    </div>
  );

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
      <DynamicForm
        fields={formFields}
        form={form}
        onSubmit={handleSubmit}
        loading={formLoading}
        renderCustomFooter={renderCustomFooter}
        config={{
          layout: "vertical",
          spacing: "comfortable",
          showResetButton: false,
          showSubmitButton: false,
        }}
      />
    </div>
  );
};

export default UserForm;
